import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  HStack,
  Img,
  Image,
  Skeleton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { AiOutlineMore } from "react-icons/ai";
import ReactPaginate from "react-paginate";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import iconDetail from "../../assets/images/employee/detail.svg";
import { formatPrice } from "../../utils/utils";
import SearchableSelect from "../../components/selectForm";
import SearchableSelectMulti from "../../components/selectForm/mutiSelect";
import { RankCustomers } from "../../utils/contants";
import { useConditionalLists } from "../../hooks/useCustomer";
import DetailCustomerCare from "./detailCustomerCare";

// Màu badge theo hạng khách hàng.
const RANK_STYLE = {
  "Kim cương": { bg: "#E0F2FE", color: "#0284C7" },
  Vàng: { bg: "#FEF3C7", color: "#D97706" },
  Bạc: { bg: "#F1F5F9", color: "#64748B" },
  "Thành viên": { bg: "#F3F4F6", color: "#6B7280" },
};

const CENTER_KEYS = [
  "stt",
  "transactionCount",
  "wishlistCount",
  "transactedProjectCount",
  "rank",
];

// Nới rộng + giữ 1 dòng cho các cột dễ bị tràn xuống.
const COLUMN_STYLE = {
  name: { minW: "200px", whiteSpace: "nowrap" },
  phone: { minW: "140px", whiteSpace: "nowrap" },
  customerType: { minW: "150px", whiteSpace: "nowrap" },
  rankScore: { minW: "170px", whiteSpace: "nowrap" },
};

// Các cột có thể sắp xếp (giá trị tính toán).
const SORTABLE_KEYS = [
  "transactionCount",
  "wishlistCount",
  "transactedProjectCount",
  "rankScore",
  "rank",
];

const SortIcon = ({ active, order }) => {
  if (active && order === "desc") return <FaSortDown color="#1A9ED0" />;
  if (active && order === "asc") return <FaSortUp color="#1A9ED0" />;
  return <FaSort color="#C9CED6" />;
};

const RankBadge = ({ rank }) => {
  const style = RANK_STYLE[rank] || RANK_STYLE["Thành viên"];
  return (
    <Box
      display="inline-block"
      p={"4px 12px"}
      borderRadius={"999px"}
      backgroundColor={style.bg}
      color={style.color}
      fontSize={{ base: "13px" }}
      fontWeight={600}
      whiteSpace={"nowrap"}
    >
      {rank}
    </Box>
  );
};

const TableCustomerCare = ({
  tableData,
  search,
  setSearch,
  rank,
  setRank,
  wishlistProjectId,
  setWishlistProjectId,
  transactedProjectId,
  setTransactedProjectId,
  sortBy,
  sortOrder,
  onSort,
  totalPages,
  handlePageChange,
  isLoading,
  isRefetching,
  currentPage,
  entries,
  startEntry,
  endEntry,
}) => {
  const onChangeSearch = useCallback(
    (e) => {
      setSearch(e.target.value);
    },
    [setSearch],
  );

  const { projectList } = useConditionalLists("", { project: true });
  const projectOptions = useMemo(
    () =>
      Object.entries(projectList || {}).map(([key, value]) => ({ key, value })),
    [projectList],
  );

  // Giữ hiển thị lựa chọn (chip + highlight) khi bảng remount lúc refetch.
  const selectedWishlistOptions = useMemo(
    () => projectOptions.filter((o) => (wishlistProjectId || []).includes(o.key)),
    [projectOptions, wishlistProjectId],
  );
  const selectedTransactedOptions = useMemo(
    () =>
      projectOptions.filter((o) => (transactedProjectId || []).includes(o.key)),
    [projectOptions, transactedProjectId],
  );

  const [detailId, setDetailId] = useState(null);

  const renderCell = (item, key, index) => {
    switch (key) {
      case "action":
        return (
          <Menu>
            <MenuButton
              as={Button}
              background="none"
              _hover={{ backgroundColor: "none" }}
              _active={{ backgroundColor: "none" }}
              minW={"auto"}
              p={"0"}
            >
              <AiOutlineMore fontSize={"20px"} />
            </MenuButton>
            <MenuList
              fontSize={{ base: "14px" }}
              fontWeight={500}
              color={"var(--color-info-employee)"}
            >
              <MenuItem gap={"12px"} onClick={() => setDetailId(item?._id)}>
                <Image src={iconDetail} /> Xem chi tiết
              </MenuItem>
            </MenuList>
          </Menu>
        );
      case "stt":
        return index + 1;
      case "rankScore":
        return `${formatPrice(item?.rankScore || 0)} đ`;
      case "rank":
        return <RankBadge rank={item?.rank} />;
      default:
        return item?.[key] ?? "-";
    }
  };

  // Bộ lọc hiển thị ngay dưới tiêu đề cột (giống trang Quản lý nhân viên).
  const columnHeaderFilters = {
    rank: (
      <SearchableSelect
        isShowSearch={false}
        options={RankCustomers}
        initialSelectedOption={{ key: null, value: "Tất cả" }}
        setValue={setRank}
        selectedKey={rank}
        width="100%"
        minW="150px"
      />
    ),
    wishlistCount: (
      <SearchableSelectMulti
        isShowSearch={true}
        options={projectOptions}
        initialSelectedOption={selectedWishlistOptions}
        setValue={setWishlistProjectId}
        placeholder="Tất cả"
        width="100%"
        minW="160px"
      />
    ),
    transactedProjectCount: (
      <SearchableSelectMulti
        isShowSearch={true}
        options={projectOptions}
        initialSelectedOption={selectedTransactedOptions}
        setValue={setTransactedProjectId}
        placeholder="Tất cả"
        width="100%"
        minW="160px"
      />
    ),
  };

  return (
    <>
      <Flex flexDirection={"column"} gap={{ base: "16px" }}>
      <Flex
        backgroundColor="var(--color-main)"
        borderRadius={{ base: "12px" }}
        flexDirection={"column"}
      >
        <Flex
          alignItems={"center"}
          justifyContent={"flex-start"}
          gap={{ base: "16px" }}
          flexWrap={"wrap"}
          p={"16px 16px 12px 16px"}
        >
          <InputGroup
            maxW="42vw"
            minW={"320px"}
            borderRadius={{ base: "8px" }}
            w={{ base: "42vw" }}
          >
            <Input
              placeholder="Nhập tên khách hàng, số điện thoại, email, mã KH ..."
              type="text"
              value={search}
              borderRadius={{ base: "8px" }}
              border={{ base: "1px solid var(--color-boder)" }}
              onChange={onChangeSearch}
            />
            <InputRightElement
              borderTopRightRadius={"8px"}
              borderBottomRightRadius={"8px"}
              pointerEvents="none"
              backgroundImage="var(--color-button)"
              cursor={"pointer"}
            >
              <SearchIcon color="var(--color-main)" />
            </InputRightElement>
          </InputGroup>
        </Flex>

        <Box width={"100%"} height={"100%"}>
          <TableContainer w={"100%"} maxH="calc(100vh - 320px)" overflowY="auto">
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              <Table w={"100%"} variant="unstyled">
                <Thead
                  position="sticky"
                  top="0"
                  zIndex="1"
                  w={"100%"}
                  h={"41px"}
                  fontWeight="400"
                  backgroundColor="#F9FAFD"
                >
                  <Tr h={{ base: "41px" }}>
                    {tableData.headers.map((e, index) => {
                      const sortable = SORTABLE_KEYS.includes(e.key);
                      const centered = CENTER_KEYS.includes(e.key);
                      return (
                        <Td
                          p={"16px 16px 8px 16px"}
                          key={index}
                          border={"none"}
                          color={"#51607B"}
                          fontSize={{ base: "14px" }}
                          minW={COLUMN_STYLE[e.key]?.minW}
                          cursor={sortable ? "pointer" : "default"}
                          userSelect={"none"}
                          onClick={sortable ? () => onSort(e.key) : undefined}
                          _hover={sortable ? { color: "#1A9ED0" } : undefined}
                        >
                          <Flex
                            alignItems={"center"}
                            justifyContent={centered ? "center" : "flex-start"}
                            gap={"6px"}
                            fontWeight={"bold"}
                            whiteSpace={"nowrap"}
                          >
                            {e.label}
                            {sortable && (
                              <SortIcon
                                active={sortBy === e.key}
                                order={sortOrder}
                              />
                            )}
                          </Flex>
                        </Td>
                      );
                    })}
                  </Tr>
                  <Tr backgroundColor="#F9FAFD">
                    {tableData.headers.map((e, index) => (
                      <Td
                        key={index}
                        p={"0px 16px 12px 16px"}
                        border={"none"}
                        verticalAlign={"top"}
                      >
                        {columnHeaderFilters[e.key] || null}
                      </Td>
                    ))}
                  </Tr>
                </Thead>
                <Tbody w={"100%"} h={"100%"}>
                  {tableData.data?.length > 0 ? (
                    tableData.data.map((item, rowIndex) => (
                      <Tr key={item?._id || rowIndex} h={"64px"}>
                        {tableData.headers.map((header, colIndex) => (
                          <Td
                            p={"16px 16px 8px 16px"}
                            key={colIndex}
                            bg={
                              rowIndex % 2 === 0
                                ? "var(--color-main)"
                                : "var(--fill-avatar)"
                            }
                            verticalAlign={"middle"}
                            minW={COLUMN_STYLE[header.key]?.minW}
                          >
                            <Box
                              fontSize={{ base: "14px" }}
                              lineHeight={"19.6px"}
                              textAlign={
                                CENTER_KEYS.includes(header.key)
                                  ? "center"
                                  : "start"
                              }
                              fontWeight={500}
                              color={"#293755"}
                              whiteSpace={
                                COLUMN_STYLE[header.key]?.whiteSpace || "normal"
                              }
                            >
                              {renderCell(item, header.key, rowIndex)}
                            </Box>
                          </Td>
                        ))}
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td
                        colSpan={tableData.headers.length}
                        textAlign="center"
                        padding={"40px 0"}
                      >
                        Không có dữ liệu
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Skeleton>
          </TableContainer>

          <Box mt={10} mr={5} mb={5} ml={5}>
            <Flex alignItems={"center"} justifyContent={"space-between"}>
              Showing {startEntry} to {endEntry} of {entries} entries
              <HStack spacing={2} justify="flex-end">
                <ReactPaginate
                  className="pagination-custom"
                  pageCount={totalPages}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  onPageChange={handlePageChange}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                  breakClassName={"ellipsis"}
                  breakLabel={"..."}
                  previousLabel={
                    <Img w={"16px"} minW={"16px"} h={"16px"} src={chevronLeft} />
                  }
                  nextLabel={
                    <Img w={"16px"} minW={"16px"} h={"16px"} src={chevronRight} />
                  }
                  renderOnZeroPageCount={null}
                  forcePage={currentPage - 1}
                />
              </HStack>
            </Flex>
          </Box>
        </Box>
      </Flex>
      </Flex>

      <DetailCustomerCare
        isOpen={!!detailId}
        customerId={detailId}
        onClose={() => setDetailId(null)}
      />
    </>
  );
};

export default TableCustomerCare;
